import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import { usePermissions } from "@utils/permissions";
import {
  FieldValue,
  UG_TABS,
  UG_TAB_PERMISSION,
  UG_TAB_TO_ENTITY,
  UGEntityType,
  UGMeta,
  UGTabFilters,
  UGTabKey,
} from "../types";

const isValidTab = (value: string | null): value is UGTabKey =>
  !!value && (UG_TABS as string[]).includes(value);

const EMPTY_FIELD_FILTERS: Record<string, FieldValue> = {};
const EMPTY_META: UGMeta = {};

export interface UsersGroupsPageState {
  activeTab: UGTabKey;
  allowedTabs: UGTabKey[];
  entityType: UGEntityType;
  search: string;
  fieldFilters: Record<string, FieldValue>;
  sort: string | null;
  page: number;
  setActiveTab: (next: UGTabKey) => void;
  update: (patch: Partial<UGTabFilters>) => void;
}

export const useUsersGroupsPageState = (): UsersGroupsPageState => {
  const { user } = useAuth();
  const { filters: contextFilters, setFilters } = useFilters();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = usePermissions();

  const allowedTabs = useMemo(
    () => UG_TABS.filter((tab) => can(UG_TAB_PERMISSION[tab])),
    [can],
  );
  const isAllowedTab = (tab: UGTabKey | null): tab is UGTabKey =>
    !!tab && allowedTabs.includes(tab);

  const userScope = user?.id_user ?? "anon";

  const metaFilterId = `${userScope}_v2_usersgroups_meta`;
  const meta = (contextFilters[metaFilterId] || EMPTY_META) as UGMeta;
  const persistedTab: UGTabKey = meta.activeTab ?? "users";

  const urlTabParam = searchParams.get("tab");
  const urlTab: UGTabKey | null =
    isValidTab(urlTabParam) && isAllowedTab(urlTabParam) ? urlTabParam : null;
  // Se il tab richiesto (URL o persistito) non è consentito, ripiega sul primo
  // tab per cui l'utente ha il permesso di lettura.
  const activeTab: UGTabKey =
    urlTab ??
    (isAllowedTab(persistedTab) ? persistedTab : (allowedTabs[0] ?? "users"));

  useEffect(() => {
    if (urlTab && urlTab !== persistedTab) {
      setFilters(metaFilterId, { ...meta, activeTab: urlTab });
    }
  }, [urlTab, persistedTab, meta, metaFilterId, setFilters]);

  const entityType = UG_TAB_TO_ENTITY[activeTab];

  const filterId = `${userScope}_v2_usersgroups_${activeTab}`;
  const current = (contextFilters[filterId] || {}) as UGTabFilters;

  const search = current.search ?? "";
  const fieldFilters = current.fieldFilters ?? EMPTY_FIELD_FILTERS;
  const sort = current.sort ?? "most-recent";
  const page = current.page ?? 1;

  const setActiveTab = (next: UGTabKey) => {
    setFilters(metaFilterId, { ...meta, activeTab: next });
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", next);
    setSearchParams(nextParams, { replace: true });
  };

  const update = (patch: Partial<UGTabFilters>) => {
    setFilters(filterId, { ...current, ...patch });
  };

  return {
    activeTab,
    allowedTabs,
    entityType,
    search,
    fieldFilters,
    sort,
    page,
    setActiveTab,
    update,
  };
};
